/* ==========================================================================
   CT PDP Buy Box  -  behaviour

   Loaded once per page and cached across every product template. Each
   section instance carries its own config in a <script type="application/json"
   data-bb-config> tag, so nothing here is Liquid-interpolated.

   Implements the PDF spec:
     2  review count scrolls to the reviews widget
     3  "View Nutrition Table" opens an image overlay
     4  pack change repaints price / savings / offers / stock / sticky bar
     4b size change selects the matching variant, sold-out sizes disabled
     5  freebie appears / disappears on cart-value eligibility
     6  offers recalculate on every pack and quantity change
     7  add to cart opens the side cart, buy now goes to checkout
     8  pincode driven estimated delivery via GoKwik KwikShip
    10  savings pop-up shows the merchant-uploaded image, no redirect
    12  cross-sell adds without leaving the PDP
   ========================================================================== */
(function () {
  'use strict';

  var DEFAULT_MONEY = '{{ amount }}';

  function makeMoney(cfg) {
    var fmt = cfg.moneyFormat || DEFAULT_MONEY;
    var rx = /\{\{\s*(\w+)\s*\}\}/;

    function group(cents, precision, thousands, decimal) {
      precision = isNaN(precision = Math.abs(precision)) ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      var parts = (cents / 100).toFixed(precision).split('.');
      return parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands) +
        (parts[1] ? decimal + parts[1] : '');
    }

    /* Indian grouping: last three digits, then pairs. */
    function indian(cents, precision, thousands, decimal) {
      precision = isNaN(precision = Math.abs(precision)) ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      var parts = (cents / 100).toFixed(precision).split('.');
      var whole = parts[0];
      var sign = '';
      if (whole.charAt(0) === '-') { sign = '-'; whole = whole.substring(1); }
      var last3 = whole.length > 3 ? whole.substring(whole.length - 3) : whole;
      var rest = whole.length > 3 ? whole.substring(0, whole.length - 3) : '';
      if (rest) {
        whole = rest.replace(/\B(?=(\d{2})+(?!\d))/g, thousands) + thousands + last3;
      } else {
        whole = last3;
      }
      return sign + whole + (parts[1] ? decimal + parts[1] : '');
    }

    return function money(cents) {
      cents = Math.round(Number(cents) || 0);
      var match = fmt.match(rx);
      var key = match ? match[1] : 'amount';
      var value;
      switch (key) {
        case 'amount_no_decimals':
          value = cfg.isINR ? indian(cents, 0) : group(cents, 0); break;
        case 'amount_with_comma_separator':
          value = group(cents, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator':
          value = group(cents, 0, '.', ','); break;
        default:
          value = cfg.isINR ? indian(cents, 2) : group(cents, 2);
      }
      return fmt.replace(rx, value);
    };
  }


  /* Horizontal paging, animated here rather than by the browser.

     scrollTo with behavior smooth is unreliable on a container with mandatory
     scroll snap: Chrome frequently never starts it, so the arrow appears dead
     and the slide only arrives when a fallback forces it - measured at around
     half a second of nothing followed by a jump. Both the zoom popup and the
     testimonial carousel are snap containers and both were doing it.

     Stepping scrollLeft ourselves is predictable: snap and CSS smoothing are
     held off for the duration and restored at the end, so the resting state is
     still snapped. rAF is used where it runs and a timer stands in where it
     does not, and anyone who has asked for less motion simply gets the jump.
  */
  function smoothScrollX(el, to, done) {
    /* The pristine values are remembered against the element the first time it
       is ever animated, not captured per call. Pressing the arrow again while
       a scroll is still running used to capture the PAUSED values - snap off -
       and put those back at the end, leaving the track permanently unsnapped. */
    if (el.__bbSnapWas === undefined) {
      el.__bbSnapWas = el.style.scrollSnapType;
      el.__bbBehaviorWas = el.style.scrollBehavior;
    }
    /* and the one already running has to stop, or two of them fight over
       scrollLeft and the movement stutters */
    if (el.__bbAnimStop) el.__bbAnimStop();

    var from = el.scrollLeft;
    var dist = to - from;
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var cancelled = false;

    function restore() {
      el.style.scrollSnapType = el.__bbSnapWas;
      el.style.scrollBehavior = el.__bbBehaviorWas;
    }
    function finish() {
      el.__bbAnimStop = null;
      el.scrollLeft = to;
      restore();
      if (done) done();
    }

    el.style.scrollSnapType = 'none';
    el.style.scrollBehavior = 'auto';

    if (reduced || Math.abs(dist) < 2) return finish();

    /* long enough to read as movement, short enough not to feel sluggish */
    var ms = Math.min(520, Math.max(240, Math.abs(dist) * 0.6));
    var startedAt = null;
    var hasRaf = typeof window.requestAnimationFrame === 'function';
    var now = function () { return window.performance && performance.now ? performance.now() : Date.now(); };

    /* Which clock is driving. rAF is preferred, but it does not run everywhere
       - a background tab, a context that never composites - and a slide that
       waits forever for a frame that never comes just looks broken. If the
       first frame has not arrived shortly, a timer takes over; late rAF
       callbacks are then ignored so the two cannot both drive it. */
    var driver = hasRaf ? 'raf' : 'timer';
    var watchdog = null;

    el.__bbAnimStop = function () {
      cancelled = true;
      if (watchdog) clearTimeout(watchdog);
      el.__bbAnimStop = null;
    };

    function step(t, source) {
      if (cancelled || source !== driver) return;
      if (startedAt === null) {
        startedAt = t;
        if (watchdog) { clearTimeout(watchdog); watchdog = null; }
      }
      var k = Math.min(1, (t - startedAt) / ms);
      /* easeInOutQuad - starts and stops gently, no jolt at either end */
      var eased = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
      el.scrollLeft = from + dist * eased;
      if (k >= 1) {
        if (watchdog) clearTimeout(watchdog);
        return finish();
      }
      if (driver === 'raf') {
        requestAnimationFrame(function (ts) { step(ts, 'raf'); });
      } else {
        setTimeout(function () { step(now(), 'timer'); }, 16);
      }
    }

    if (driver === 'raf') {
      requestAnimationFrame(function (ts) { step(ts, 'raf'); });
      watchdog = setTimeout(function () {
        if (cancelled || startedAt !== null) return;
        driver = 'timer';
        step(now(), 'timer');
      }, 120);
    } else {
      step(now(), 'timer');
    }
  }

  function initBuyBox(root) {
    if (!root || root.hasAttribute('data-bb-init')) return;

    /* Parse BEFORE claiming the init flag: if the config is ever malformed the
       box must stay re-initialisable and must say so, rather than silently
       rendering a fully inert buy box with no console trace. */
    var cfgNode = root.querySelector('[data-bb-config]');
    var CFG;
    try {
      CFG = JSON.parse(cfgNode.textContent) || {};
    } catch (e) {
      if (window.console) console.error('[ct-pdp-buybox] config JSON is invalid, buy box not initialised:', e);
      return;
    }
    root.setAttribute('data-bb-init', '1');

    var variants = [];
    try {
      variants = JSON.parse(root.querySelector('[data-bb-variants]').textContent) || [];
    } catch (e) {}

    var money = makeMoney(CFG);
    var $ = function (sel) { return root.querySelector(sel); };
    var $$ = function (sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };

    var form = $('form.bb-form');
    var idInput = $('[data-bb-variant-id]');
    var qtyInput = $('[data-bb-qty]');
    var priceEl = $('[data-bb-price]');
    var compareEl = $('[data-bb-compare]');
    var saveWrap = $('[data-bb-save-wrap]');
    var saveEl = $('[data-bb-save]');
    var atcBtn = $('[data-bb-atc]');
    var atcText = $('[data-bb-atc-text]');
    var buyBtn = $('[data-bb-buynow]');
    var errEl = $('[data-bb-error]');
    var freebieEl = $('[data-bb-freebie]');
    var stockEl = $('[data-bb-stock]');
    var mainImg = $('[data-bb-main]');
    var sticky = document.querySelector('[data-bb-sticky="' + CFG.sectionId + '"]');

    /* Keeps the sticky bar's chip showing whatever is currently selected.
       Declared here rather than beside the rest of the sticky wiring because
       selectVariant runs earlier in the file and calls it. */
    function syncStickyChip(v) {
      if (!sticky || !v) return;
      var label = sticky.querySelector('[data-bbst-chip-label]');
      var img = sticky.querySelector('[data-bbst-chip-img]');
      if (label) {
        /* Prefer the pack card's own label so the chip and the cards agree:
           the cards are labelled from a metafield, not from variant.title.
           Products without pack cards (size, single option) fall back. */
        var card = root.querySelector('[data-bb-pack][data-variant-id="' + v.id + '"]');
        var name = card && card.querySelector('.bb-pack__name');
        var text = (name && name.textContent.trim()) || v.title;
        if (text) label.textContent = text;
      }
      if (img && v.featured_image) { img.removeAttribute('srcset'); img.src = v.featured_image; }
    }

    var current = null;
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(idInput && idInput.value)) { current = variants[i]; break; }
    }
    if (!current) current = variants[0] || null;

    function qty() {
      if (!qtyInput) return 1;
      var q = parseInt(qtyInput.value, 10);
      if (isNaN(q) || q < 1) q = 1;
      if (q > 999) q = 999;
      return q;
    }

    /* The freebie is an ORDER-value offer, so eligibility is what is already in
       the cart plus the line about to be added - not the line on its own.
       Seeded from Liquid at render, refreshed after anything is added. */
    var cartTotal = Number(CFG.cartTotal) || 0;

    function orderTotal(line) { return cartTotal + line; }

    /* What the freebie threshold is measured against. Default is the selected
       pack alone, so choosing a cheaper pack takes the freebie away again;
       including the cart meant one item already in it unlocked the strip for
       every pack and it never reacted to the selection. */
    function freebieTotal(line) {
      return CFG.freebieScope === 'cart' ? orderTotal(line) : line;
    }
    function freebieOn(line) {
      return CFG.freebieMin > 0 && freebieTotal(line) >= CFG.freebieMin;
    }

    function refreshCartTotal() {
      return fetch(rootUrl() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (cart && typeof cart.total_price === 'number') {
            cartTotal = cart.total_price;
            render();
          }
        })
        .catch(function () {});
    }

    /* ---------------- offers published for the whole catalogue ------------
       A CT Buy Box Offers section in the header group publishes its list as
       JSON. Liquid cannot read another section's settings, so the rows are
       placed here instead - same panel, same position, same markup as the
       ones Liquid renders, so everything downstream treats them alike. */
    var ICON_OPEN = '<svg viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M7 1.2h3.8V5M10.8 1.2L5.6 6.4M9.2 7.4v2.4a1 1 0 01-1 1H2.2a1 1 0 01-1-1V3.8a1 1 0 011-1h2.4"' +
      ' stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    var ICON_COPY = '<svg viewBox="0 0 12 13" fill="none" aria-hidden="true" focusable="false">' +
      '<rect x="3.6" y="0.6" width="7.8" height="8.6" rx="1.4" stroke="currentColor" stroke-width="1.2"/>' +
      '<path d="M8.4 11.2v.2a1.4 1.4 0 01-1.4 1.4H2a1.4 1.4 0 01-1.4-1.4V4.6A1.4 1.4 0 012 3.2h.2"' +
      ' stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';

    /* The coupon card, built to the same markup the ct-buybox-offer snippet
       renders - change one, change the other. Lines holding a [[token]] go
       out empty with their template in data-bb-offer-tpl and are filled by
       renderOffers with everything else. */
    function couponHtml(o) {
      var style = /^(green|dark|white)$/.test(o.style || '') ? o.style : 'green';
      var tokenLine = function (cls, v) {
        if (!v) return '';
        return /\[\[/.test(v)
          ? '<span class="' + cls + '" data-bb-offer-tpl="' + esc(v) + '"></span>'
          : '<span class="' + cls + '">' + v + '</span>';
      };
      var btn = '';
      if (o.code) {
        btn += '<button type="button" class="bb-coupon__btn bb-coupon__btn--copy" data-bb-copy="' + esc(o.code) + '"' +
          ' aria-label="Copy discount code ' + esc(o.code) + '">' + ICON_COPY +
          '<span data-bb-copy-label>' + esc(o.copy || 'COPY') + '</span></button>';
      }
      if (o.chip) {
        var cls = 'bb-coupon__btn bb-coupon__btn--' + style;
        if (o.action === 'savings_popup') {
          if (o.image || CFG.hasSavingsImage) {
            btn += '<button type="button" class="' + cls + '" data-bb-open="savings"' +
              (o.image ? ' data-chip-image="' + esc(o.image) + '"' : '') + '>' +
              ICON_OPEN + '<span>' + esc(o.chip) + '</span></button>';
          }
        } else if (o.action === 'link' && o.link) {
          btn += '<a class="' + cls + '" href="' + esc(o.link) + '"' +
            (o.new_tab === false ? '' : ' target="_blank" rel="noopener"') + '>' +
            ICON_OPEN + '<span>' + esc(o.chip) + '</span></a>';
        } else {
          btn += '<span class="' + cls + ' bb-coupon__btn--tag">' + esc(o.chip) + '</span>';
        }
      }
      var note = o.note ? '<span class="bb-coupon__rule" aria-hidden="true"></span>' +
        tokenLine('bb-coupon__note', o.note) : '';
      return '<div class="bb-coupon" data-bb-offer' +
        ' data-visible-when="' + esc(o.when || 'always') + '"' +
        ' data-offer-amount="' + (parseInt(o.amount, 10) || 0) + '"' +
        ' data-offer-rule="' + esc(o.amount_rule || 'at_least') + '"' +
        ' data-template="' + esc(o.text || '') + '"' +
        ' data-template-alt="' + esc(o.alt || '') + '">' +
        (o.stub ? '<span class="bb-coupon__stub" aria-hidden="true"><span class="bb-coupon__stub-label">' +
          esc(o.stub) + '</span></span>' : '') +
        '<div class="bb-coupon__body"><div class="bb-coupon__row"><span class="bb-coupon__text">' +
        tokenLine('bb-coupon__support', o.support) +
        '<span class="bb-coupon__title" data-bb-offer-text></span></span>' + btn + '</div>' +
        note + '</div></div>';
    }

    function offerRowHtml(o) {
      if (root.querySelector('.bb-offers--coupons')) return couponHtml(o);
      var chip = '';
      if (o.chip) {
        if (o.action === 'savings_popup') {
          /* nothing to open means no chip, rather than one that does nothing */
          if (o.image || CFG.hasSavingsImage) {
            var imgAttr = o.image ? ' data-chip-image="' + esc(o.image) + '"' : '';
            chip = '<button type="button" class="bb-offer__chip" data-bb-open="savings"' + imgAttr + '>' + esc(o.chip) + '</button>';
          }
        } else if (o.action === 'link' && o.link) {
          chip = '<a class="bb-offer__chip" href="' + esc(o.link) + '">' + esc(o.chip) + '</a>';
        } else {
          chip = '<span class="bb-offer__chip">' + esc(o.chip) + '</span>';
        }
      }
      return '<div class="bb-offer" data-bb-offer' +
        ' data-visible-when="' + esc(o.when || 'always') + '"' +
        ' data-offer-amount="' + (parseInt(o.amount, 10) || 0) + '"' +
        ' data-offer-rule="' + esc(o.amount_rule || 'at_least') + '"' +
        ' data-template="' + esc(o.text || '') + '"' +
        ' data-template-alt="' + esc(o.alt || '') + '">' +
        '<span data-bb-offer-text></span>' + chip + '</div>';
    }
    function esc(v) {
      return String(v == null ? '' : v)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
    function anyShared(mine, want) {
      if (!mine || !want) return false;
      for (var i = 0; i < want.length; i++) {
        for (var j = 0; j < mine.length; j++) {
          /* loose on purpose: an id may arrive as a number or a string */
          if (String(mine[j]) === String(want[i])) return true;
        }
      }
      return false;
    }
    function targetsThisProduct(o) {
      if (!o || !o.text) return false;
      if (o.scope === 'products') {
        return anyShared([CFG.productId], o.products) ||
               anyShared([CFG.productHandle], o.product_handles);
      }
      if (o.scope === 'collections') {
        return anyShared(CFG.collectionIds, o.collections) ||
               anyShared(CFG.collectionHandles, o.collection_handles);
      }
      return true;
    }
    function applyPublishedOffers() {
      var body = root.querySelector('.bb-offers__body');
      if (!body) return;
      var node = document.querySelector('script[type="application/json"][data-ct-offers]');
      if (node) {
        var list = null;
        try { list = JSON.parse(node.textContent); } catch (e) { list = null; }
        /* Only treat the section as authoritative when it actually parsed.
           Malformed JSON leaves whatever the template rendered in place rather
           than wiping the panel on a syntax error. */
        if (Array.isArray(list)) {
          var mine = [];
          for (var i = 0; i < list.length; i++) {
            if (targetsThisProduct(list[i])) mine.push(list[i]);
          }
          /* Assigned even when empty. The section is the one place offers are
             managed, so "nothing targets this product" has to be able to clear
             rows the product template rendered - otherwise an offer meant for
             other products stays visible here. */
          body.innerHTML = mine.map(offerRowHtml).join('');
        }
      }
      /* nothing resolved from anywhere - drop the empty panel */
      if (!body.querySelector('[data-bb-offer]')) {
        var panel = body.closest ? body.closest('.bb-offers') : null;
        if (panel && panel.parentNode) panel.parentNode.removeChild(panel);
      }
    }
    applyPublishedOffers();

    /* ---------------- render ---------------- */
    var offerEls = $$('[data-bb-offer]');

    function render() {
      if (!current) return;
      var q = qty();
      /* sitewide code offer: the code price, and the undiscounted price as the
         struck reference when there is no higher MRP - as the Liquid draws it */
      var web = CFG.webPct || 0;
      var unit = web > 0 ? Math.floor(current.price * (100 - web) / 100) : current.price;
      var ref = (current.compare_at_price && current.compare_at_price > current.price)
        ? current.compare_at_price : (web > 0 ? current.price : 0);
      var line = unit * q;
      var compare = ref > unit ? ref * q : 0;

      if (priceEl) priceEl.textContent = money(line);
      if (saveWrap) {
        if (compare > line) {
          saveWrap.hidden = false;
          if (compareEl) compareEl.textContent = money(compare);
          if (saveEl) saveEl.textContent = CFG.savePrefix + ' ' + money(compare - line);
        } else {
          saveWrap.hidden = true;
        }
      }

      var soldOut = !current.available;
      if (atcBtn) atcBtn.disabled = soldOut;
      if (buyBtn) buyBtn.disabled = soldOut;
      if (atcText) atcText.textContent = soldOut ? CFG.soldOutLabel : CFG.atcLabel;
      if (idInput) { idInput.value = current.id; idInput.disabled = soldOut; }

      if (sticky) {
        var sp = sticky.querySelector('[data-bbst-price]');
        if (sp) sp.innerHTML = money(line) + (compare > line ? ' <s>' + money(compare) + '</s>' : '');
        var sa = sticky.querySelector('[data-bbst-atc]');
        var sb = sticky.querySelector('[data-bbst-buy]');
        if (sa) sa.disabled = soldOut;
        if (sb) sb.disabled = soldOut;
      }

      if (stockEl && CFG.lowStockShow) {
        var inv = typeof current.inventory_quantity === 'number' ? current.inventory_quantity : null;
        var tracked = current.inventory_management === 'shopify';
        var show = tracked && inv !== null && inv > 0 && CFG.lowStockAt > 0 && inv <= CFG.lowStockAt;
        stockEl.classList.toggle('is-on', !!show);
        if (show) stockEl.textContent = CFG.lowStockText.replace('[[qty]]', String(inv));
      }

      renderOffers(line, compare, q);
      if (freebieEl) freebieEl.hidden = !freebieOn(line);
    }

    function renderOffers(line, compare, q) {
      if (!offerEls.length) return;
      var indiv = CFG.comboIndividual > 0 ? CFG.comboIndividual * q : compare;
      var savings = indiv > line ? indiv - line : 0;
      var pct = indiv > 0 ? Math.round((savings / indiv) * 100) : 0;
      var prepaid = CFG.prepaidPct > 0 ? Math.round(line * (1 - (CFG.prepaidPct / 100))) : line;
      var eligible = freebieOn(line);

      /* Each figure gets its own span so it can be styled on its own. Liquid
         bakes the identical wrappers in at render, so a row repainted here on
         a pack change keeps the same hooks. */
      function amt(cls, v) {
        return '<span class="bb-offer__amt bb-offer__' + cls + '">' + v + '</span>';
      }

      var map = {
        price: amt('price', money(line)),
        mrp: amt('mrp', money(compare || line)),
        savings: amt('savings', money(savings)),
        savings_percent: amt('pct', String(pct)),
        prepaid: amt('prepaid', money(prepaid)),
        freebie_min: amt('freebie-min', money(CFG.freebieMin)),
        qty: amt('qty', String(q))
      };

      offerEls.forEach(function (el) {
        var when = el.getAttribute('data-visible-when') || 'always';
        var show = true;
        if (when === 'freebie_eligible') show = eligible;
        else if (when === 'freebie_not_eligible') show = !eligible;
        else if (when === 'on_sale') show = savings > 0;

        /* Amount condition. Everything here is integer minor units - the line
           total already is, and the setting is converted in Liquid - so there
           is no float arithmetic to round the wrong way. */
        var need = parseInt(el.getAttribute('data-offer-amount'), 10) || 0;
        if (show && need > 0) {
          var rule = el.getAttribute('data-offer-rule') || 'at_least';
          show = (rule === 'exactly') ? (line === need) : (line >= need);
        }
        el.hidden = !show;

        /* Fill the text even while the row is hidden. Liquid bakes it in, but
           rows built from a published offer list start empty, and skipping
           hidden ones left the first row blank until something made it show. */
        var tpl = el.getAttribute('data-template') || '';
        var alt = el.getAttribute('data-template-alt') || '';
        if (when === 'always' && alt && eligible) tpl = alt;
        var fill = function (t) {
          return t.replace(/\[\[(\w+)\]\]/g, function (whole, key) {
            return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : whole;
          });
        };
        var out = fill(tpl);
        /* the offer lines were written as list items; a card title drops the
           dash, as the snippet does */
        if (el.classList.contains('bb-coupon')) out = out.replace(/^\s*-\s+/, '');
        var target = el.querySelector('[data-bb-offer-text]');
        if (target) target.innerHTML = out;
        /* the coupon's other lines, when they hold tokens */
        Array.prototype.forEach.call(el.querySelectorAll('[data-bb-offer-tpl]'), function (sub) {
          sub.innerHTML = fill(sub.getAttribute('data-bb-offer-tpl') || '');
        });
      });
    }

    /* ---------------- variant selection ---------------- */
    /* No group parameter any more - every variant control is synced below
       regardless of which one was clicked. */
    function selectVariant(vid) {
      var found = null;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(vid)) { found = variants[i]; break; }
      }
      if (!found) return;
      current = found;

      /* Every control that selects a variant is synced, not just the group
         that was clicked. Matching is by variant id, so a group the product
         does not use simply has no buttons and nothing happens - and picking
         a pack in the sticky bar now moves the main pack row with it instead
         of the two disagreeing. */
      ['data-bb-pack', 'data-bb-size', 'data-bbst-opt'].forEach(function (g) {
        /* data-bbst-opt lives in the sticky bar, which is rendered outside
           .bb-root - and $$() is scoped to root, so the sticky menu's tick
           never moved. Each group is looked up in the scope it lives in. */
        var scope = g === 'data-bbst-opt' ? sticky : root;
        if (!scope) return;
        Array.prototype.forEach.call(scope.querySelectorAll('[' + g + ']'), function (b) {
          var on = String(b.getAttribute('data-variant-id')) === String(vid);
          b.classList.toggle('is-active', on);
          b.setAttribute(g === 'data-bbst-opt' ? 'aria-selected' : 'aria-checked', on ? 'true' : 'false');
        });
      });

      syncStickyChip(found);

      render();

      if (mainImg && found.featured_image) {
        mainImg.removeAttribute('srcset');
        mainImg.src = found.featured_image;
        if (galWrap) galWrap.setAttribute('data-bb-media-id', found.featured_media_id || '');
        /* keep the active thumbnail, and so the dots and arrows, on the
           image now showing */
        if (found.featured_media_id) {
          var vThumbs = $$('[data-bb-thumb]');
          for (var vt = 0; vt < vThumbs.length; vt++) {
            if (String(vThumbs[vt].getAttribute('data-media-id')) === String(found.featured_media_id)) {
              vThumbs.forEach(function (x) { x.classList.remove('is-active'); });
              vThumbs[vt].classList.add('is-active');
              break;
            }
          }
          galSync();
        }
      }
      try {
        if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) {
          window.ShopifyAnalytics.meta.selectedVariantId = found.id;
        }
      } catch (e) {}
      try {
        if (window.history && history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', found.id);
          history.replaceState({}, '', url.toString());
        }
      } catch (e) {}
      root.dispatchEvent(new CustomEvent('bb:variant:change', { bubbles: true, detail: { variant: found } }));
    }

    $$('[data-bb-pack]').forEach(function (b) {
      b.addEventListener('click', function () {
        if (!b.disabled) selectVariant(b.getAttribute('data-variant-id'));
      });
    });
    $$('[data-bb-size]').forEach(function (b) {
      b.addEventListener('click', function () {
        if (!b.disabled) selectVariant(b.getAttribute('data-variant-id'));
      });
    });

    /* ---------------- quantity ----------------
       The sticky bar can carry its own stepper. It is not a second quantity:
       both steppers write to [data-bb-qty], which is the input the cart and
       Buy Now handlers already read, and syncQtyButtons mirrors the value back
       out to the sticky field. That keeps one number on the page however the
       shopper changes it, including when the buy box's own stepper is hidden
       and [data-bb-qty] is a hidden input. */
    var stickyQtyInput = sticky && sticky.querySelector('[data-bbst-qty-input]');

    function syncQtyButtons() {
      var q = qty();
      var minus = root.querySelector('[data-bb-qty-step="-1"]');
      if (minus) minus.disabled = q <= 1;
      var stMinus = sticky && sticky.querySelector('[data-bbst-qty-step="-1"]');
      if (stMinus) stMinus.disabled = q <= 1;
      if (stickyQtyInput && String(q) !== stickyQtyInput.value) stickyQtyInput.value = q;
    }

    function stepQty(by) {
      if (!qtyInput) return;
      qtyInput.value = Math.min(999, Math.max(1, qty() + by));
      syncQtyButtons();
      render();
    }

    $$('[data-bb-qty-step]').forEach(function (b) {
      b.addEventListener('click', function () {
        stepQty(parseInt(b.getAttribute('data-bb-qty-step'), 10) || 1);
      });
    });

    /* the sticky bar is rendered outside .bb-root, so $$ does not reach it */
    if (sticky) {
      Array.prototype.slice.call(sticky.querySelectorAll('[data-bbst-qty-step]')).forEach(function (b) {
        b.addEventListener('click', function () {
          stepQty(parseInt(b.getAttribute('data-bbst-qty-step'), 10) || 1);
        });
      });
      if (stickyQtyInput) {
        ['input', 'change'].forEach(function (ev) {
          stickyQtyInput.addEventListener(ev, function () {
            if (!qtyInput) return;
            var q = parseInt(stickyQtyInput.value, 10);
            if (isNaN(q) || q < 1) q = 1;
            if (q > 999) q = 999;
            qtyInput.value = q;
            syncQtyButtons();
            render();
          });
        });
      }
    }
    if (qtyInput && qtyInput.type !== 'hidden') {
      ['input', 'change'].forEach(function (ev) {
        qtyInput.addEventListener(ev, function () {
          var q = parseInt(qtyInput.value, 10);
          if (isNaN(q) || q < 1) q = 1;
          if (q > 999) q = 999;
          if (String(q) !== qtyInput.value) qtyInput.value = q;
          syncQtyButtons();
          render();
        });
      });
    }

    /* ---------------- cart ---------------- */
    function showError(msg) {
      if (!errEl) return;
      errEl.textContent = msg || '';
      errEl.classList.toggle('is-on', !!msg);
    }
    function rootUrl() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }
    function openCartDrawer(state) {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function' && state && state.sections) {
        try { drawer.renderContents(state); return true; } catch (e) {}
      }
      return false;
    }
    /* Resolves when the drawer is actually showing, so the button keeps
       spinning through the gap between "request finished" and "the shopper can
       see their cart" - that gap is where the second click happens. Capped, so
       a drawer that never opens cannot strand the button. */
    function whenDrawerVisible() {
      var drawer = document.querySelector('cart-drawer');
      function open() {
        return drawer && (drawer.classList.contains('active') ||
          drawer.classList.contains('animate') || drawer.hasAttribute('open') ||
          drawer.getAttribute('aria-hidden') === 'false');
      }
      if (!drawer || open()) return Promise.resolve();
      return new Promise(function (resolve) {
        var done = false;
        function finish() {
          if (done) return;
          done = true;
          obs.disconnect();
          clearTimeout(t);
          resolve();
        }
        var obs = new MutationObserver(function () { if (open()) finish(); });
        obs.observe(drawer, { attributes: true, attributeFilter: ['class', 'open', 'aria-hidden'] });
        var t = setTimeout(finish, 3000);
      });
    }

    function addToCart(payload, btn, doneLabel) {
      if (btn) { btn.disabled = true; btn.classList.add('is-busy'); }
      return fetch(rootUrl() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [payload],
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname
        })
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res.status && res.status !== 200) {
            showError(res.description || res.message || 'Could not add to cart.');
            return null;
          }
          showError('');
          if (CFG.cartRedirect) { window.location.href = rootUrl() + 'cart'; return res; }
          openCartDrawer(res);
          /* Cross-sell adds change the order value, so the freebie and any
             offer keyed on freebie eligibility have to be re-evaluated. */
          refreshCartTotal();
          if (btn && doneLabel) {
            var lbl = btn.querySelector('[data-bb-cross-add-text]');
            if (lbl) {
              lbl.textContent = doneLabel;
              setTimeout(function () { lbl.textContent = CFG.addLabel; }, 1800);
            }
          }
          return res;
        })
        .catch(function () { showError('Network error. Please try again.'); return null; })
        .then(function (r) {
          return whenDrawerVisible().then(function () {
            if (btn) { btn.disabled = false; btn.classList.remove('is-busy'); }
            return r;
          });
        });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!current || !current.available) return;
        addToCart({ id: current.id, quantity: qty() }, atcBtn, null);
      });
    }

    /* onBuyNowClick calls window.gokwikSdk.triggerLoadingScreen(), which the
       vendor bundle never defines - it arrives with the SDK after the
       gokwikLoaded event. So the function existing is not enough. */
    /* GoKwik's third argument is a LIST of line items, not one item. Its
       addCartBuyNow does `a.length ? {items: a} : querySelector(
       window.productFormSelector)`, so an object made `a.length` undefined and
       it fell through to productFormSelector - '.product__info-container',
       which is Dawn markup the buy box never renders. querySelector returned
       null, the vendor threw "Cannot read properties of null (reading
       'querySelectorAll')", no /cart/add.js was ever sent, and its loading
       screen sat there forever. Confirmed against the live PDP both ways:
       an object throws and sends nothing, an array POSTs
       {"items":[{id,quantity}]}, gets a 200 and opens the checkout iframe. */
    function gkItems(q) {
      return [{ id: current.id, quantity: q }];
    }

    function gokwikReady() {
      return typeof window.onBuyNowClick === 'function' &&
        window.gokwikSdk && typeof window.gokwikSdk.triggerLoadingScreen === 'function';
    }

    function buyNow(btn) {
      if (!current || !current.available) return;
      var q = qty();
      if (idInput) { idInput.value = current.id; idInput.disabled = false; }
      try {
        if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) {
          window.ShopifyAnalytics.meta.selectedVariantId = current.id;
        }
      } catch (e) {}
      /* GoKwik drives this button itself: onBuyNowClick takes its first
         element child as a loader, disables the button and re-enables it 3s
         later. Nothing here should add .is-busy on top - that would hide the
         vendor's loader along with the label. */
      if (gokwikReady()) {
        window.onBuyNowClick(btn, false, gkItems(q));
        return;
      }

      /* merchant.integration.js is deferred and the SDK lands later still, so
         an early click used to fall straight through to Shopify's checkout -
         silently skipping GoKwik, and COD with it. Wait for gokwikLoaded and
         only fall back if it never arrives. */
      var loader = btn.querySelector('.addloadr');
      var settled = false;

      function finish(useGokwik) {
        if (settled) return;
        settled = true;
        clearTimeout(cap);
        window.removeEventListener('gokwikLoaded', onLoaded);
        if (loader) loader.style.display = 'none';
        btn.disabled = false;
        if (useGokwik && gokwikReady()) {
          window.onBuyNowClick(btn, false, gkItems(q));
        } else {
          addToCart({ id: current.id, quantity: q }, btn, null).then(function () {
            window.location.href = rootUrl() + 'checkout';
          });
        }
      }
      function onLoaded() { finish(true); }

      if (loader) loader.style.display = 'flex';
      btn.disabled = true;
      window.addEventListener('gokwikLoaded', onLoaded);
      var cap = setTimeout(function () { finish(false); }, 5000);
    }
    if (buyBtn) buyBtn.addEventListener('click', function () { buyNow(buyBtn); });

    /* ---------------- sticky bar ---------------- */
    if (sticky && atcBtn) {
      var stickyAtc = sticky.querySelector('[data-bbst-atc]');
      var stickyBuy = sticky.querySelector('[data-bbst-buy]');
      if (stickyAtc) stickyAtc.addEventListener('click', function () {
        if (!current || !current.available) return;
        addToCart({ id: current.id, quantity: qty() }, stickyAtc, null);
      });
      if (stickyBuy) stickyBuy.addEventListener('click', function () { buyNow(stickyBuy); });

      /* ---- pack picker ---- */
      var picker = sticky.querySelector('[data-bbst-picker]');
      var chip = sticky.querySelector('[data-bbst-chip]');
      var menu = sticky.querySelector('[data-bbst-menu]');

      function closePicker() {
        if (!picker || !menu) return;
        menu.hidden = true;
        picker.classList.remove('is-open');
        if (chip) chip.setAttribute('aria-expanded', 'false');
      }
      function openPicker() {
        if (!picker || !menu) return;
        menu.hidden = false;
        picker.classList.add('is-open');
        if (chip) chip.setAttribute('aria-expanded', 'true');
        /* a long variant list opens scrolled to whatever is selected */
        var on = menu.querySelector('.bbst__opt.is-active');
        if (on && on.scrollIntoView) on.scrollIntoView({ block: 'nearest' });
      }

      if (chip && menu) {
        chip.addEventListener('click', function (e) {
          e.stopPropagation();
          if (menu.hidden) openPicker(); else closePicker();
        });

        sticky.querySelectorAll('[data-bbst-opt]').forEach(function (opt) {
          opt.addEventListener('click', function () {
            if (opt.disabled) return;
            selectVariant(opt.getAttribute('data-variant-id'));
            closePicker();
          });
        });

        /* dismiss on anything outside, and on Escape */
        document.addEventListener('click', function (e) {
          if (!menu.hidden && !picker.contains(e.target)) closePicker();
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && !menu.hidden) { closePicker(); if (chip) chip.focus(); }
        });
        /* scrolling far enough hides the bar itself, so an open menu floating
           over the page would be orphaned */
        window.addEventListener('scroll', function () {
          if (!menu.hidden && !sticky.classList.contains('is-on')) closePicker();
        }, { passive: true });
      }

      if ('IntersectionObserver' in window) {
        /* No rootMargin. It used to be '0px 0px -100% 0px', which shrinks the
           root's bottom edge by a whole viewport height and collapses the root
           rect to zero height at y=0. Against a degenerate root the target can
           never produce a non-zero intersection area, so isIntersecting stays
           false and no further threshold crossings are delivered - the bar was
           never toggled on.

           Plain viewport root instead: entries arrive whenever the button
           enters or leaves the viewport, and boundingClientRect.top separates
           "scrolled past it" (above, top < 0) from "not reached yet" (below),
           which is the distinction the bar actually needs. */
        new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            var show = !en.isIntersecting && en.boundingClientRect.top < 0;
            sticky.classList.toggle('is-on', show);
            sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
          });
        }).observe(atcBtn);

        var footer = document.querySelector('footer');

        if (footer) {
          var footerObs = new IntersectionObserver(function(entries) {
            if (entries[0].isIntersecting) {
              sticky.classList.remove('is-on');
              sticky.setAttribute('aria-hidden', 'true');
            }
          }, { threshold: 0.01 });

          footerObs.observe(footer);
        }

      }
    }

    

    /* ---------------- cross-sell ---------------- */
    $$('[data-bb-cross]').forEach(function (item) {
      var sel = item.querySelector('[data-bb-cross-variant]');
      var priceOut = item.querySelector('[data-bb-cross-price]');
      var mrpOut = item.querySelector('[data-bb-cross-mrp]');
      var addBtn = item.querySelector('[data-bb-cross-add]');
      function paint() {
        if (!sel || sel.tagName !== 'SELECT') return;
        var opt = sel.options[sel.selectedIndex];
        if (!opt) return;
        var p = parseInt(opt.getAttribute('data-price'), 10) || 0;
        var c = parseInt(opt.getAttribute('data-compare'), 10) || 0;
        if (priceOut) priceOut.textContent = money(p);
        if (mrpOut) {
          if (c > p) { mrpOut.textContent = money(c); mrpOut.hidden = false; }
          else { mrpOut.hidden = true; }
        }
      }
      if (sel && sel.tagName === 'SELECT') sel.addEventListener('change', paint);
      if (addBtn) addBtn.addEventListener('click', function () {
        var vid = sel ? sel.value : null;
        if (vid) addToCart({ id: vid, quantity: 1 }, addBtn, CFG.addedLabel);
      });
    });

    /* ---------------- modals ---------------- */
    function toggleModal(name, open) {
      var m = root.querySelector('[data-bb-modal="' + name + '"]');
      if (!m) return;
      m.classList.toggle('is-open', open);
      m.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.body.style.overflow = open ? 'hidden' : '';
    }

    /* A Limited Period Offers chip published from the header group can carry
       its own image (data-chip-image), meant for every product that offer
       targets. The savings modal itself is section-rendered once per product
       from a metafield/section-setting image, so a chip's own image has to be
       swapped in at click time instead - and swapped back out again for
       whichever chip is clicked next, so one offer's picture never lingers
       behind another's. */
    var savingsBox = root.querySelector('[data-bb-modal="savings"] .bb-modal__box');
    var savingsImg = savingsBox && savingsBox.querySelector('img');
    var savingsHint = savingsBox && savingsBox.querySelector('.bb-modal__hint');
    var savingsDefaultSrc = savingsImg ? savingsImg.getAttribute('src') || '' : '';
    function setSavingsImage(overrideSrc) {
      if (!savingsImg) return;
      var src = overrideSrc || savingsDefaultSrc;
      if (src) {
        savingsImg.src = src;
        savingsImg.hidden = false;
        if (savingsHint) savingsHint.hidden = true;
      } else {
        savingsImg.hidden = true;
        if (savingsHint) savingsHint.hidden = false;
      }
    }

    $$('[data-bb-open]').forEach(function (b) {
      b.addEventListener('click', function () {
        var name = b.getAttribute('data-bb-open');
        if (name === 'savings') setSavingsImage(b.getAttribute('data-chip-image') || '');
        toggleModal(name, true);
      });
    });
    /* Customer review photos. Delegated rather than bound per button: the
       reviews are a carousel whose slides come from three different sources,
       and delegation covers all of them without caring which one rendered. */
    var revPicImg = root.querySelector('[data-bb-revpic-img]');
    var revPicBox = revPicImg && revPicImg.closest('.bb-revpic__box');
    var revPicReq = 0;
    var revPicCache = {};

    /* Decode a full-size photo off-screen. Resolves with the same URL so the
       caller can check it is still the one wanted. Cached per URL, so the
       second open of the same photo is instant. */
    function loadRevPic(url) {
      if (revPicCache[url]) return revPicCache[url];
      revPicCache[url] = new Promise(function (resolve) {
        var im = new Image();
        im.onload = function () { resolve(url); };
        im.onerror = function () { resolve(url); };
        im.src = url;
      });
      return revPicCache[url];
    }

    /* Warm the cache the first time a pointer reaches a review's photo strip,
       so by the time anyone taps, the big versions are usually already in. */
    root.addEventListener('pointerover', function (e) {
      var strip = e.target.closest && e.target.closest('.bb-review__pics');
      if (!strip || strip._bbWarmed) return;
      strip._bbWarmed = true;
      Array.prototype.slice.call(strip.querySelectorAll('[data-bb-revpic]')).forEach(function (b) {
        var u = b.getAttribute('data-full');
        if (u) loadRevPic(u);
      });
    }, { passive: true });

    root.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-bb-revpic]');
      if (!btn || !revPicImg) return;
      var full = btn.getAttribute('data-full') || '';
      var thumb = btn.querySelector('img');
      var thumbSrc = thumb ? (thumb.currentSrc || thumb.getAttribute('src') || '') : '';
      if (!full) full = thumbSrc;
      if (!full) return;

      /* Show the thumbnail at once - it is already decoded - then upgrade to
         the full image when it arrives. A stale response (the shopper tapped
         another photo in the meantime) is dropped by the request counter, so
         the overlay can never show photo 1 after photo 2 was chosen. */
      var req = ++revPicReq;
      revPicImg.src = thumbSrc || full;
      if (revPicBox) revPicBox.classList.toggle('is-loading', full !== thumbSrc);
      toggleModal('revpic', true);
      loadRevPic(full).then(function (url) {
        if (req !== revPicReq) return;
        revPicImg.src = url;
        if (revPicBox) revPicBox.classList.remove('is-loading');
      });
    });

    $$('[data-bb-modal-close]').forEach(function (b) {
      b.addEventListener('click', function () {
        var m = b.closest('[data-bb-modal]');
        if (m) toggleModal(m.getAttribute('data-bb-modal'), false);
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = root.querySelector('[data-bb-modal].is-open');
      if (open) toggleModal(open.getAttribute('data-bb-modal'), false);
    });

    /* ---------------- zoom popup ----------------
       Every slide is already in the DOM; this only drives which one is shown.
       The track is the scroller, so paging is scrollTo and the current index
       is read back off scrollLeft - one source of truth, which is what keeps
       the arrows, the counter and the thumbnails from disagreeing with what
       is actually on screen after a swipe. */
    var galWrap = $('[data-bb-zoom]');
    var zTrack = $('[data-bb-zoom-track]');
    var zSlides = $$('[data-bb-zoom-slide]');
    var zThumbs = $$('[data-bb-zoom-thumb]');
    var zPrev = $('[data-bb-zoom-prev]');
    var zNext = $('[data-bb-zoom-next]');
    var zAt = $('[data-bb-zoom-at]');
    var zIndex = 0;

    function zoomIndexFromScroll() {
      if (!zTrack || !zSlides.length) return 0;
      var w = zTrack.clientWidth || 1;
      return Math.max(0, Math.min(zSlides.length - 1, Math.round(zTrack.scrollLeft / w)));
    }

    var zThumbStrip = $('[data-bb-zoom-thumbs]');
    var zBusy = false;
    var zBusyTimer = null;

    function paintZoom() {
      if (zAt) zAt.textContent = String(zIndex + 1);
      zThumbs.forEach(function (t, i) { t.classList.toggle('is-active', i === zIndex); });
      if (zPrev) zPrev.disabled = zIndex <= 0;
      if (zNext) zNext.disabled = zIndex >= zSlides.length - 1;
      /* The active thumbnail is brought into view by setting scrollLeft rather
         than scrollIntoView: scrollIntoView walks every scrollable ancestor and
         cancelled the track's own smooth scroll mid-flight, which left the
         counter on the new slide and the image still on the old one. */
      if (zThumbStrip && zThumbs[zIndex]) {
        var t = zThumbs[zIndex];
        var min = t.offsetLeft + t.offsetWidth - zThumbStrip.clientWidth;
        if (zThumbStrip.scrollLeft < min) zThumbStrip.scrollLeft = min;
        if (zThumbStrip.scrollLeft > t.offsetLeft) zThumbStrip.scrollLeft = t.offsetLeft;
      }
    }

    /* Paging the track, defensively.

       Two things bite here. `behavior: 'auto'` is not instant - the stylesheet
       sets scroll-behavior smooth on the track and 'auto' means "whatever the
       CSS says" - so opening the popup sets scrollLeft with smoothing off
       instead. And Chrome will sometimes drop a programmatic smooth scroll on
       a mandatory-snap container outright: it never starts, and the arrow
       appears dead while the counter has already moved on. Snap is switched
       off while we drive the scroll, the landing is checked, and a scroll that
       did not arrive is put there directly. */
    function goZoom(i, instant) {
      if (!zTrack || !zSlides.length) return;
      zIndex = Math.max(0, Math.min(zSlides.length - 1, i));
      var target = zIndex * zTrack.clientWidth;
      zBusy = true;
      if (zBusyTimer) clearTimeout(zBusyTimer);

      function done() { zBusy = false; }

      if (instant) {
        var prevSnap = zTrack.style.scrollSnapType;
        var prevBehavior = zTrack.style.scrollBehavior;
        zTrack.style.scrollSnapType = 'none';
        zTrack.style.scrollBehavior = 'auto';
        zTrack.scrollLeft = target;
        zTrack.style.scrollSnapType = prevSnap;
        zTrack.style.scrollBehavior = prevBehavior;
        paintZoom();
        return done();
      }

      smoothScrollX(zTrack, target, done);
      paintZoom();
    }

    if (zTrack) {
      var zTick = null;
      zTrack.addEventListener('scroll', function () {
        if (zBusy) return;
        if (zTick) clearTimeout(zTick);
        zTick = setTimeout(function () {
          var i = zoomIndexFromScroll();
          if (i !== zIndex) { zIndex = i; paintZoom(); }
        }, 60);
      }, { passive: true });
    }
    if (zPrev) zPrev.addEventListener('click', function () { goZoom(zIndex - 1); });
    if (zNext) zNext.addEventListener('click', function () { goZoom(zIndex + 1); });
    zThumbs.forEach(function (t) {
      t.addEventListener('click', function () { goZoom(parseInt(t.getAttribute('data-index'), 10) || 0); });
    });
    document.addEventListener('keydown', function (e) {
      var open = root.querySelector('[data-bb-modal="zoom"].is-open');
      if (!open) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); goZoom(zIndex - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); goZoom(zIndex + 1); }
    });

    /* Open on whichever image the gallery is showing, matched by media id so
       it survives thumbnails, variant swaps and any reordering. */
    function openZoomAt(mediaId) {
      var start = 0;
      if (mediaId) {
        for (var i = 0; i < zSlides.length; i++) {
          if (String(zSlides[i].getAttribute('data-media-id')) === String(mediaId)) { start = i; break; }
        }
      }
      toggleModal('zoom', true);
      goZoom(start, true);
    }

    /* ---------------- gallery ---------------- */
    $$('[data-bb-thumb]').forEach(function (t) {
      t.addEventListener('click', function () {
        $$('[data-bb-thumb]').forEach(function (x) { x.classList.remove('is-active'); });
        t.classList.add('is-active');
        var wrap = $('[data-bb-gal-main]');
        if (!wrap) return;
        /* Replaces only the picture. Setting innerHTML on the frame took the
           arrows, the zoom badge and the dots with it, so after one video
           the gallery had no way back but the thumbnails. */
        var setMedia = function (html) {
          var olds = wrap.querySelectorAll('[data-bb-main], [data-bb-gal-media], .bb-placeholder');
          for (var k = 0; k < olds.length; k++) olds[k].parentNode.removeChild(olds[k]);
          wrap.insertAdjacentHTML('afterbegin', html);
        };
        var type = t.getAttribute('data-media-type');
        if (type && type !== 'image') {
          var host = t.getAttribute('data-external');
          var extId = t.getAttribute('data-external-id');
          var src = t.getAttribute('data-video');
          if (host === 'youtube' && extId) {
            setMedia('<iframe data-bb-gal-media src="https://www.youtube.com/embed/' + encodeURIComponent(extId) +
              '?autoplay=1&rel=0" title="Product video" allow="autoplay; encrypted-media" allowfullscreen loading="lazy"></iframe>');
          } else if (host === 'vimeo' && extId) {
            setMedia('<iframe data-bb-gal-media src="https://player.vimeo.com/video/' + encodeURIComponent(extId) +
              '?autoplay=1" title="Product video" allow="autoplay" allowfullscreen loading="lazy"></iframe>');
          } else if (src) {
            setMedia('<video data-bb-gal-media src="' + src + '" controls playsinline preload="none" poster="' +
              t.getAttribute('data-src') + '"></video>');
          }
          wrap.classList.add('is-video');
          mainImg = null;
          galSync();
          return;
        }
        wrap.classList.remove('is-video');
        if (!wrap.querySelector('[data-bb-main]')) {
          setMedia('<img data-bb-main width="1100" height="1100" alt="" decoding="async">');
        }
        mainImg = wrap.querySelector('[data-bb-main]');
        if (mainImg) { mainImg.removeAttribute('srcset'); mainImg.src = t.getAttribute('data-src'); }
        /* so the popup opens on this image and not back at the first one */
        if (wrap) wrap.setAttribute('data-bb-media-id', t.getAttribute('data-media-id') || '');
        /* hoisted - defined with the arrows below */
        galSync();
      });
    });

    /* ---------------- gallery swipe ----------------

       A phone has no thumbnail hover and the strip below the image reads as
       decoration, so the main image itself takes a horizontal drag and steps
       through the same media.

       The step is performed by clicking the thumbnail rather than by setting
       .src here. Everything the handler above already does - swapping in a
       video or an iframe, moving .is-active, and updating the media id the
       zoom popup opens on - therefore keeps happening in exactly one place
       instead of being duplicated and drifting out of step with it.

       touchstart/move/end rather than pointer events, because these fire only
       on touch input: the desktop gallery is left completely untouched.      */
    var galMain = $('[data-bb-gal-main]');
    var galThumbs = $$('[data-bb-thumb]');
    var galPrev = $('[data-bb-gal-prev]');
    var galNext = $('[data-bb-gal-next]');
    var galDots = $$('[data-bb-gal-dot]');
    var galSwiped = false;

    /* index of the thumbnail currently showing in the main frame */
    function galAt() {
      for (var i = 0; i < galThumbs.length; i++) {
        if (galThumbs[i].classList.contains('is-active')) return i;
      }
      return 0;
    }

    /* Clamped rather than wrapped: running off the end of a product's images
       should feel like the end, not silently restart it. */
    function galSync() {
      /* a variant swap can land here before the gallery below is set up */
      if (!galThumbs) return;
      var at = galAt();
      if (galPrev) galPrev.disabled = at <= 0;
      if (galNext) galNext.disabled = at >= galThumbs.length - 1;
      (galDots || []).forEach(function (d, i) {
        d.classList.toggle('is-active', i === at);
        if (i === at) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
    }

    /* One step, shared by the arrows and the swipe. Clicking the thumbnail is
       what actually moves the gallery, so video slides, the .is-active state
       and the media id the zoom popup opens on all stay handled in the one
       thumbnail handler above rather than being duplicated here. */
    function galStep(dir) {
      var target = galAt() + dir;
      if (target < 0 || target > galThumbs.length - 1) return false;
      galThumbs[target].click();
      /* past the sixth image the active thumbnail is off the end of the strip,
         so the strip would stop showing where you are */
      if (galThumbs[target].scrollIntoView) {
        galThumbs[target].scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
      galSync();
      return true;
    }

    if (galThumbs.length > 1) {
      galSync();
      /* the arrows sit inside .bb-gal__main, whose own click opens the zoom
         popup - without this every arrow press would open it too */
      if (galPrev) {
        galPrev.addEventListener('click', function (e) { e.stopPropagation(); galStep(-1); });
      }
      if (galNext) {
        galNext.addEventListener('click', function (e) { e.stopPropagation(); galStep(1); });
      }
    }

    if (galMain && galThumbs.length > 1) {
      var swipeX = 0;
      var swipeY = 0;
      var swiping = false;

      galMain.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) return;
        swipeX = e.touches[0].clientX;
        swipeY = e.touches[0].clientY;
        swiping = true;
        galSwiped = false;
      }, { passive: true });

      galMain.addEventListener('touchmove', function (e) {
        if (!swiping) return;
        /* a mostly-vertical drag is the page being scrolled, not a slide
           change - let go of it so the scroll is not interfered with */
        var dx = Math.abs(e.touches[0].clientX - swipeX);
        var dy = Math.abs(e.touches[0].clientY - swipeY);
        if (dy > dx) swiping = false;
      }, { passive: true });

      galMain.addEventListener('touchend', function (e) {
        if (!swiping) return;
        swiping = false;

        /* while a video is playing the drag belongs to its controls */
        if (!galMain.querySelector('[data-bb-main]')) return;

        var dx = e.changedTouches[0].clientX - swipeX;
        /* below this it is a tap with a shaky finger, which should still zoom */
        if (Math.abs(dx) < 40) return;

        if (galStep(dx < 0 ? 1 : -1)) galSwiped = true;
      }, { passive: true });
    }

    /* Dots sit inside the frame, whose own click opens the zoom popup. */
    galDots.forEach(function (d, i) {
      d.addEventListener('click', function (e) {
        e.stopPropagation();
        if (galThumbs[i]) galThumbs[i].click();
        galSync();
      });
    });

    /* Grid tiles: an image opens the popup on itself; a video, or any tile
       when zoom is off, goes to the main frame the way its thumbnail would. */
    $$('[data-bb-gal-tile]').forEach(function (tile) {
      tile.addEventListener('click', function () {
        var idx = parseInt(tile.getAttribute('data-index'), 10) || 0;
        var isImage = (tile.getAttribute('data-media-type') || 'image') === 'image';
        if (isImage && galWrap && zSlides.length) {
          openZoomAt(tile.getAttribute('data-media-id'));
          return;
        }
        if (galThumbs[idx]) galThumbs[idx].click();
        galSync();
        var frame = $('[data-bb-gal-main]');
        if (frame && frame.scrollIntoView) {
          frame.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      });
    });

    if (galWrap && zSlides.length) {
      galWrap.addEventListener('click', function () {
        /* a video playing in the gallery is not something to open a still of */
        if (!galWrap.querySelector('[data-bb-main]')) return;
        /* a drag ends by firing a click, which would open the popup on every
           single swipe. This listener is registered after the swipe handlers
           above, so the flag is already set by the time it runs. */
        if (galSwiped) { galSwiped = false; return; }
        openZoomAt(galWrap.getAttribute('data-bb-media-id'));
      });
    }

    /* ---------------- coupon codes ----------------
       navigator.clipboard is unavailable on http and in some in-app browsers,
       and it rejects when the document is not focused - which is exactly the
       moment someone taps a button. So the modern call is tried and its
       failure falls through to the old execCommand path rather than leaving
       the shopper with a button that silently does nothing. */
    function copyText(text) {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text).catch(function () { return legacyCopy(text); });
      }
      return legacyCopy(text);
    }
    function legacyCopy(text) {
      return new Promise(function (resolve, reject) {
        var ta = document.createElement('textarea');
        ta.value = text;
        /* off-screen rather than display:none - a hidden textarea cannot be
           selected, and a visible one would scroll the page on focus */
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '-1000px';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        try {
          ta.select();
          ta.setSelectionRange(0, ta.value.length);
          document.execCommand('copy') ? resolve() : reject();
        } catch (e) { reject(e); }
        document.body.removeChild(ta);
      });
    }

    /* Delegated: cards built from the published offer list arrive after
       anything bound per button would have run. */
    root.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('[data-bb-copy]') : null;
      if (!btn || !root.contains(btn)) return;
      var code = btn.getAttribute('data-bb-copy') || '';
      if (!code) return;
      var label = btn.querySelector('[data-bb-copy-label]');
      if (label && !btn.hasAttribute('data-bb-copy-orig')) {
        btn.setAttribute('data-bb-copy-orig', label.textContent);
      }
      var original = btn.getAttribute('data-bb-copy-orig') || '';
      copyText(code).then(function () {
        btn.classList.add('is-copied');
        if (label) label.textContent = 'COPIED';
      }).catch(function () {
        /* Say so rather than pretending. The code is on screen, so the
           shopper can still type it. */
        if (label) label.textContent = code;
      }).then(function () {
        if (btn._bbRevert) clearTimeout(btn._bbRevert);
        btn._bbRevert = setTimeout(function () {
          btn.classList.remove('is-copied');
          if (label) label.textContent = original;
        }, 1800);
      });
    });

    /* ---------------- reviews carousel ---------------- */
    var track = $('[data-bb-rev-track]');
    if (track) {
      var slides = $$('[data-bb-rev-slide]');
      var dots = $$('[data-bb-rev-dot]');
      var prev = $('[data-bb-rev-prev]');
      var next = $('[data-bb-rev-next]');

      /* Where a slide sits within the track's own scroll space.

         offsetLeft was being used for this, but the track is not positioned,
         so offsetLeft is measured from the page rather than from the track -
         it read 372 / 847 / 1322 where the real offsets are 0 / 475 / 950.
         The arrows scrolled to those numbers, landed on the wrong slide or
         clamped at the end, and the dots pointed at whichever slide was
         nearest to a meaningless figure. A rect delta is right whatever the
         positioning happens to be. */
      var slideLeft = function (i) {
        if (!slides[i]) return 0;
        return Math.round(
          slides[i].getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft
        );
      };

      var revBusy = false;
      var revTimer = null;

      var activeIndex = function () {
        var best = 0, bd = Infinity;
        slides.forEach(function (s, i) {
          var d = Math.abs(slideLeft(i) - track.scrollLeft);
          if (d < bd) { bd = d; best = i; }
        });
        return best;
      };
      var syncNav = function () {
        var idx = activeIndex();
        dots.forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= (track.scrollWidth - track.clientWidth - 2);
      };

      var go = function (i) {
        if (!slides[i]) return;
        revBusy = true;
        if (revTimer) clearTimeout(revTimer);
        smoothScrollX(track, slideLeft(i), function () {
          revBusy = false;
          syncNav();
        });
        syncNav();
      };

      if (prev) prev.addEventListener('click', function () { go(Math.max(0, activeIndex() - 1)); });
      if (next) next.addEventListener('click', function () { go(Math.min(slides.length - 1, activeIndex() + 1)); });
      dots.forEach(function (d) {
        d.addEventListener('click', function () { go(parseInt(d.getAttribute('data-bb-rev-dot'), 10) || 0); });
      });
      track.addEventListener('scroll', function () {
        if (revBusy) return;
        window.clearTimeout(track._bbT);
        track._bbT = window.setTimeout(syncNav, 90);
      }, { passive: true });
      syncNav();
    }

    $$('[data-bb-scroll-reviews]').forEach(function (revJump) {
      revJump.addEventListener('click', function () {
        var target = root.querySelector('[data-bb-reviews]');
        if (!target && CFG.reviewsAnchor) {
          try { target = document.querySelector(CFG.reviewsAnchor); } catch (e) {}
        }
        if (target && target.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    /* ---------------- accordions ---------------- */
    $$('[data-bb-acc-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var wrap = btn.closest('[data-bb-acc]');
        if (!wrap) return;
        var open = !wrap.classList.contains('is-open');
        wrap.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });

    /* ---------------- 8. delivery: GoKwik KwikShip + KwikPass ---------------- */
    if (CFG.edd && CFG.edd.on) {
      var eddText = $('[data-bb-edd-text]');
      var eddForm = $('[data-bb-edd-form]');
      var eddInput = $('[data-bb-edd-input]');
      var eddGo = $('[data-bb-edd-go]');
      var eddChange = $('[data-bb-edd-change]');
      var eddErr = $('[data-bb-edd-error]');
      var eddBusy = false;

      var cookie = function (n) {
        var v = '; ' + document.cookie;
        var p = v.split('; ' + n + '=');
        return p.length === 2 ? p.pop().split(';').shift() : null;
      };
      /* KwikPass drops the session token in localStorage on the storefront origin. */
      var kwikToken = function () {
        var keys = ['KWIKSESSIONTOKEN', 'KWIKUSERTOKEN', 'kwik_session_token', 'gk_token'];
        for (var i = 0; i < keys.length; i++) {
          try { var v = localStorage.getItem(keys[i]); if (v) return v; } catch (e) {}
        }
        return cookie('KWIKSESSIONTOKEN') || cookie('gk_token') || null;
      };
      var baseHeaders = function () {
        var h = { 'Content-Type': 'application/json', Accept: 'application/json, text/plain, */*' };
        if (CFG.edd.auth) h.Authorization = CFG.edd.auth;
        var t = kwikToken();
        if (t) h['x-gokwik-token'] = t;
        return h;
      };
      var eddError = function (msg) {
        if (!eddErr) return;
        eddErr.textContent = msg || '';
        eddErr.classList.toggle('is-on', !!msg);
      };
      var showForm = function (on) {
        if (eddForm) eddForm.hidden = !on;
        if (eddText) eddText.style.display = on ? 'none' : '';
        if (eddChange) eddChange.hidden = on;
        if (on && eddInput) eddInput.focus();
      };
      var eddItems = function () {
        if (!current) return [];
        return [{ sku: current.sku || current.barcode || String(current.id), quantity: qty() }];
      };

      var checkEdd = function (pin) {
        if (eddBusy) return;
        pin = String(pin || '').trim();
        if (!/^\d{6}$/.test(pin)) { eddError('Please enter a valid 6-digit pincode.'); return; }
        eddBusy = true;
        eddError('');
        if (eddText) eddText.textContent = 'Checking delivery...';

        var payload = {
          m_id: CFG.edd.mid,
          pincode: pin,
          cart_total: current ? current.price * qty() : 0,
          type: 'pdp',
          items: eddItems()
        };
        var sid = cookie('_shopify_s') || cookie('cart');
        if (sid) payload.shopify_session_id = sid;

        fetch(CFG.edd.eddUrl, { method: 'POST', headers: baseHeaders(), body: JSON.stringify(payload) })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var d = data && data.data;
            var txt = null;
            if (Array.isArray(d) && d.length) {
              txt = d[0].text || d[0].edd_text || (d[0].edd ? 'Delivery by ' + d[0].edd : null);
            } else if (d && typeof d === 'object') {
              txt = d.text || d.edd_text || (d.edd ? 'Delivery by ' + d.edd : null);
            } else if (data) {
              txt = data.text || data.edd_text || (data.edd ? 'Delivery by ' + data.edd : null);
            }

            if (txt && txt.toLowerCase().indexOf('null') === -1) {
              if (eddText) {
                eddText.innerHTML = txt.replace(/(Fastest Delivery by|Delivery by)/i, '<b>$1</b>') +
                  ' to <b>' + pin + '</b>';
              }
              try { localStorage.setItem('bbPincode', pin); } catch (e) {}
            } else if (eddText) {
              eddText.textContent = 'Delivery is not available for ' + pin + '.';
            }
            showForm(false);
          })
          .catch(function () {
            if (eddText) eddText.textContent = 'Could not check delivery right now.';
            showForm(false);
          })
          .then(function () { eddBusy = false; });
      };

      if (eddChange) eddChange.addEventListener('click', function () { showForm(true); });
      if (eddGo) eddGo.addEventListener('click', function () { checkEdd(eddInput && eddInput.value); });
      if (eddInput) {
        eddInput.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') { e.preventDefault(); checkEdd(eddInput.value); }
        });
        eddInput.addEventListener('input', function () {
          eddInput.value = eddInput.value.replace(/\D/g, '').slice(0, 6);
        });
      }

      /* POST {m_id} to /kwikship/customer/pincode with the KwikPass token to
         recover the shopper's last used pincode, then resolve the EDD from it. */
      var prefillPincode = function () {
        var saved = null;
        try { saved = localStorage.getItem('bbPincode'); } catch (e) {}
        if (saved && /^\d{6}$/.test(saved)) { checkEdd(saved); return; }
        if (!kwikToken()) return;

        fetch(CFG.edd.pinUrl, {
          method: 'POST',
          headers: baseHeaders(),
          body: JSON.stringify({ m_id: CFG.edd.mid })
        })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            if (!data) return;
            var d = data.data;
            var pin = null;
            if (Array.isArray(d) && d.length) pin = d[0].pincode || d[0].pin_code;
            else if (d && typeof d === 'object') pin = d.pincode || d.pin_code;
            else if (typeof d === 'string') pin = d;
            if (pin && /^\d{6}$/.test(String(pin))) checkEdd(String(pin));
          })
          .catch(function () {});
      };

      if ('requestIdleCallback' in window) window.requestIdleCallback(prefillPincode, { timeout: 2500 });
      else window.setTimeout(prefillPincode, 1200);
    }

    /* ---- red price-drop tag beside "Choose Quantity" ----
       Its messages take turns: the current one slides up and fades out while
       the next rises into place. Holds still under the mouse and while the
       tab is hidden. With data-fit the tag eases to the width of each
       message; without it the grid keeps the width of the longest. */
    $$('[data-bb-drop]').forEach(function (tag) {
      var vp = tag.querySelector('.bb-drop__vp');
      var items = vp ? Array.prototype.slice.call(vp.children) : [];
      if (!items.length) return;
      var fit = tag.hasAttribute('data-fit');
      var speed = parseFloat(tag.getAttribute('data-speed')) || 500;
      var hold = (parseFloat(tag.getAttribute('data-interval')) || 2.5) * 1000;
      var at = 0;
      var paused = false;
      function setWidth(animate) {
        if (!fit) return;
        if (!animate) vp.style.transition = 'none';
        vp.style.width = items[at].offsetWidth + 'px';
        if (!animate) { void vp.offsetWidth; vp.style.transition = ''; }
      }
      setWidth(false);
      window.addEventListener('resize', function () { setWidth(false); });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { setWidth(false); });
      if (items.length < 2) return;
      tag.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') paused = true; });
      tag.addEventListener('pointerleave', function () { paused = false; });
      var timer = window.setInterval(function () {
        /* the theme editor swaps the section out from under a running timer */
        if (!document.documentElement.contains(tag)) { window.clearInterval(timer); return; }
        if (paused || document.hidden) return;
        var out = items[at];
        at = (at + 1) % items.length;
        out.classList.remove('is-on');
        out.classList.add('is-out');
        items[at].classList.add('is-on');
        setWidth(true);
        /* once it has left, park the old message back below the cell without
           animating, so its next turn rises from underneath again */
        window.setTimeout(function () {
          out.style.transition = 'none';
          out.classList.remove('is-out');
          void out.offsetWidth;
          out.style.transition = '';
        }, speed + 60);
      }, hold + speed);
    });

    syncQtyButtons();
    /* Liquid seeds the chip from the default variant, so it has to be synced
       once at init and not only on a change. */
    syncStickyChip(current);
    render();
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('.bb-root[data-bb-section]');
    Array.prototype.forEach.call(nodes, initBuyBox);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* The theme editor re-injects this <script> on every section re-render, so
     the IIFE runs again. Register the section listener only once, otherwise a
     long editing session accumulates listeners without bound. */
  if (!window.__ctBuyBoxBound) {
    window.__ctBuyBoxBound = true;
    document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  }
})();
